import { Inject, Injectable } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { environment } from '../../../environments/environment';

/**
 * DOCU-1035: Besucher- und Trichtermessung für start.docuty.de.
 *
 * Misst gegen die eigene Matomo-Instanz (matomo.docuty.de), nicht gegen das
 * Matomo-WordPress-Plugin auf docuty.de — siehe Ticketkommentar vom 18.09.2026
 * und 04-Entscheidungen/Ex WordPress.md.
 *
 * Zwei Dinge sind hier bewusst so gebaut:
 *
 * 1. **Cookielos.** `disableCookies` wird gesetzt, bevor irgendetwas gesendet
 *    wird. Damit braucht die Registrierungsseite keinen Einwilligungsbanner.
 *    Ein Banner auf genau der Seite, auf der sich jemand anmelden soll, wäre
 *    teurer als die Zahlen wert sind. Preis: Wiederkehrer werden über Tage
 *    hinweg nicht als dieselbe Person erkannt. Für die Trichterfrage („wo
 *    bricht jemand ab") spielt das keine Rolle, die läuft innerhalb einer
 *    Sitzung.
 *
 * 2. **Jeder Schritt ist ein eigener Seitenaufruf**, zusätzlich zum Ereignis.
 *    Matomos fertiger Trichterbericht ist ein kostenpflichtiges Plugin; über
 *    virtuelle Seiten-URLs liefert der normale Seiten-Bericht dieselbe Aussage
 *    kostenlos. Die Ereignisse tragen zusätzlich Tarif und Herkunft.
 *
 * Es werden ausschließlich Schrittnummern, Tarif und der `src`-Parameter
 * gesendet. Keine Eingabewerte — insbesondere nie die E-Mail-Adresse.
 */
@Injectable({ providedIn: 'root' })
export class MatomoService {

  /** Virtuelle Seitenpfade je Stepper-Index. Reihenfolge = Reihenfolge im Stepper. */
  private static readonly STEP_PATHS = [
    '/registrierung/1-konto',
    '/registrierung/2-anwendungsfall',
    '/registrierung/3-tarif',
    '/registrierung/4-zahlung',
  ];

  private initialised = false;
  /** Erreichte Schritte, damit ein Zurück-und-wieder-vor nicht doppelt zählt. */
  private readonly reportedSteps = new Set<number>();

  // Ueber DOCUMENT statt direkt ueber das globale `location`: Unter Karma laeuft
  // alles auf localhost, und die Localhost-Abschaltung unten wuerde damit jeden
  // Test still gruen faerben, ohne je eine Zeile Messcode auszufuehren.
  constructor(@Inject(DOCUMENT) private readonly doc: Document) {}

  private get location(): Location {
    return this.doc.location;
  }

  /**
   * Lädt den Tracker. Sendet bewusst **keinen** Seitenaufruf — jede Route
   * meldet sich selbst, sonst zählte die Registrierungsseite doppelt (einmal
   * als Aufruf, einmal als Schritt 1) und der Trichter wäre schief.
   *
   * Tut nichts, wenn keine URL konfiguriert ist oder die Seite lokal läuft —
   * sonst verfälschen Entwicklungsaufrufe die Zahlen. Mehrfachaufrufe sind
   * wirkungslos.
   */
  init(): void {
    if (this.initialised || !this.isEnabled()) {
      return;
    }
    this.initialised = true;

    const { url, siteId } = environment.matomo;
    const trackerBase = url.replace(/\/+$/, '');

    const paq = this.paq();
    // Reihenfolge ist hier nicht beliebig: disableCookies muss vor dem ersten
    // trackPageView stehen, sonst wird beim ersten Aufruf doch ein Cookie gesetzt.
    paq.push(['disableCookies']);
    paq.push(['enableLinkTracking']);
    paq.push(['setTrackerUrl', `${trackerBase}/matomo.php`]);
    paq.push(['setSiteId', siteId]);

    const script = this.doc.createElement('script');
    script.src = `${trackerBase}/matomo.js`;
    script.async = true;
    script.defer = true;
    // Ein blockierter oder nicht erreichbarer Tracker darf die Registrierung
    // nicht beeinträchtigen. Passiert regelmäßig (Adblocker) und ist kein Fehler.
    script.onerror = () => console.info('[matomo] Tracker nicht geladen — Messung inaktiv.');
    this.doc.head.appendChild(script);
  }

  /**
   * Gewöhnlicher Seitenaufruf für die Nebenseiten (AGB, Datenschutz).
   * Die Registrierung selbst nutzt {@link trackStep}.
   */
  trackPageView(path: string, title: string): void {
    if (!this.isEnabled()) {
      return;
    }
    const paq = this.paq();
    paq.push(['setCustomUrl', `${this.location.origin}${path}`]);
    paq.push(['setDocumentTitle', title]);
    paq.push(['trackPageView']);
  }

  /**
   * Meldet das Erreichen eines Registrierungsschritts — als virtuellen
   * Seitenaufruf (für den Trichter) und als Ereignis (für die Auswertung
   * nach Tarif und Herkunft).
   *
   * Jeder Schritt zählt nur beim ersten Erreichen. Wer zurückblättert und
   * wieder vorgeht, erzeugt keinen zweiten Eintrag — sonst sähe ein
   * unentschlossener Besucher aus wie mehrere.
   */
  trackStep(index: number): void {
    if (!this.isEnabled()) {
      return;
    }
    const path = MatomoService.STEP_PATHS[index];
    if (!path || this.reportedSteps.has(index)) {
      return;
    }
    this.reportedSteps.add(index);

    const paq = this.paq();
    paq.push(['setCustomUrl', `${this.location.origin}${path}`]);
    paq.push(['setDocumentTitle', `Registrierung – Schritt ${index + 1}`]);
    paq.push(['trackPageView']);
    paq.push(['trackEvent', 'Registrierung', 'Schritt erreicht', path, index + 1]);
  }

  /**
   * Meldet die abgeschlossene Registrierung.
   *
   * @param plan    gewählter Tarif (FREE/BASIC/PRO) — kein Personenbezug
   * @param isTrial ob eine 30-Tage-Testphase gestartet wurde (DOCU-1041)
   */
  trackRegistrationCompleted(plan: string, isTrial: boolean): void {
    if (!this.isEnabled()) {
      return;
    }
    const paq = this.paq();
    paq.push(['setCustomUrl', `${this.location.origin}/registrierung/abgeschlossen`]);
    paq.push(['setDocumentTitle', 'Registrierung – abgeschlossen']);
    paq.push(['trackPageView']);
    paq.push([
      'trackEvent',
      'Registrierung',
      isTrial ? 'Abgeschlossen (Testphase)' : 'Abgeschlossen',
      plan,
    ]);
  }

  /**
   * Meldet die Herkunft aus dem `?src=`-Parameter der Marketing-Links
   * (DOCU-1036 setzt ihn auf allen 29 CTAs).
   *
   * Matomo kennt den Referrer ohnehin, aber nicht, *welcher* Knopf auf einer
   * Landingpage geklickt wurde — genau das trägt `src`. Der Wert ist
   * Fremdeingabe aus der URL und wird deshalb gekürzt durchgereicht, analog
   * zur Regel in DOCU-1050.
   */
  trackSource(src: string | null): void {
    if (!this.isEnabled() || !src) {
      return;
    }
    this.paq().push(['trackEvent', 'Herkunft', 'Aufruf', src.slice(0, 100)]);
  }

  private isEnabled(): boolean {
    const configured = !!environment.matomo?.url && !!environment.matomo?.siteId;
    const host = this.location.hostname;
    const local = host === 'localhost' || host === '127.0.0.1';
    return configured && !local;
  }

  private paq(): unknown[][] {
    const w = (this.doc.defaultView ?? window) as unknown as { _paq?: unknown[][] };
    w._paq = w._paq || [];
    return w._paq;
  }
}
